import type { Location } from '@/store/marina-services/marina-service.dto';
import { SERVICE_CATEGORIES } from '@pages/DockagePage/helpers/serviceCategories.tsx';

/**
 * A provider's facts as the catalogue reads them. Its services, certifications and
 * addresses all hang off its locations, so every screen that shows them has to
 * flatten and dedupe the same way — that is done here once rather than per card.
 *
 * Typed on the two fields they actually read rather than on `SingleServiceProvider`,
 * because a marina is the same record under a different name: the marinas catalogue
 * draws its address, distance and services with these.
 */
type Located = { locations: Location[] };
type Distanced = { distance?: null | number };

/** Category ids across every location, in the catalogue's order, without repeats. */
export const providerCategoryIds = ({ locations }: Located): string[] => [
  ...new Set(locations.flatMap(({ service_category }) => service_category ?? [])),
];

/** The same set as labels. An id the catalogue does not know is shown as it came. */
export const providerCategoryTitles = (provider: Located): string[] =>
  providerCategoryIds(provider).map(
    (id) => SERVICE_CATEGORIES.find((category) => category.id === id)?.title ?? id,
  );

export const providerCertifications = ({ locations }: Located): string[] => [
  ...new Set(locations.flatMap(({ certifications }) => certifications ?? [])),
];

/** How far off they are, or nothing at all when the backend had no distance to give. */
export const providerDistance = ({ distance }: Distanced): string =>
  typeof distance === 'number' ? `${Math.round(distance)} miles away` : '';

/** The nearest address on record — the one fact a card has room for. */
export const providerAddress = ({ locations }: Located): string =>
  locations.find(({ location }) => location?.address)?.location?.address ?? '';

/**
 * Every place they actually work from, as the preview panel stacks them.
 *
 * A real address is the only thing that makes a location row worth showing. The
 * backend wants a location whether or not anyone picked one, so the create form
 * sends filler for the ones nobody filled in — `name: 'Name'`, no address, 0/0
 * coordinates, a dummy map_url (`ServiceProviderForms`, and the same reason the
 * card checks coordinates before it draws a map). Those rows are the workaround,
 * not a place of business, so they are left out rather than printed as "Name".
 *
 * The address is the heading for the same reason: `location.name` has no input
 * behind it anywhere in the form, so it is that filler even on a row that has a
 * genuine address.
 */
export const providerPlaces = ({ locations }: Located) =>
  locations.flatMap(({ id, location }) =>
    location?.address ? [{ id, address: location.address }] : [],
  );
